import React, { useEffect, useState, useCallback } from 'react';
import { Activity, ChevronDown, ChevronRight, Gauge, Save, CheckCircle2, XCircle } from 'lucide-react';
import { getCampaignPerformance, saveCampaignPerformance, runPerformanceCheck, getReadinessScore } from '../utils/api';

const FIELD_GROUPS = [
  { title: 'Money', fields: [['spend', 'Ad spend ($)'], ['revenue', 'Revenue ($)']] },
  { title: 'Leads', fields: [['leads', 'Leads'], ['calls', 'Calls'], ['missed_calls', 'Missed calls'], ['forms', 'Form fills']] },
  { title: 'Jobs', fields: [['booked_jobs', 'Booked jobs'], ['sold_jobs', 'Sold jobs']] },
  { title: 'Growth', fields: [['review_requests_sent', 'Review requests sent'], ['reviews_received', 'Reviews received'], ['referrals_received', 'Referrals received'], ['memberships_sold', 'Memberships sold']] },
];

const KPI_LABELS = {
  cost_per_lead: 'Cost / lead', cost_per_call: 'Cost / call', cost_per_booked_job: 'Cost / booked job',
  cost_per_sold_job: 'Cost / sold job', close_rate: 'Close rate', roi: 'ROI', roas: 'ROAS',
};

const STATUS_COLORS = {
  working: 'bg-lime/15 text-lime border-lime/40',
  needs_attention: 'bg-orange/15 text-orange border-orange/40',
  losing_money: 'bg-red-500/15 text-red-400 border-red-500/40',
  not_enough_data: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
};

export const PerformanceSection = ({ campaignId, goalData }) => {
  const [open, setOpen] = useState(false);
  const [perf, setPerf] = useState(null);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [showChecks, setShowChecks] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([getCampaignPerformance(campaignId), getReadinessScore(campaignId)]);
      setPerf(p.data);
      setReadiness(r.data);
      const m = p.data.manual || {};
      const f = {};
      FIELD_GROUPS.forEach((g) => g.fields.forEach(([k]) => { if (m[k] != null) f[k] = m[k]; }));
      setForm(f);
      setNotes(m.notes || '');
    } catch (e) { console.error('perf load failed', e); }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { notes };
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) payload[k] = Number(v); });
      const { data } = await saveCampaignPerformance(campaignId, payload);
      setPerf((p) => ({ ...p, manual: data.manual, kpis: data.kpis }));
    } catch (e) { console.error('perf save failed', e); } finally { setSaving(false); }
  };

  const runCheck = async () => {
    setChecking(true);
    try { const { data } = await runPerformanceCheck(campaignId); setCheck(data); }
    catch (e) { console.error('check failed', e); } finally { setChecking(false); }
  };

  if (!perf) return null;
  const kpis = perf.kpis || {};
  const hasKpis = Object.values(kpis).some((v) => v != null);

  return (
    <div className="mt-5 pt-5 border-t border-divider" data-testid="performance-section">
      {/* Readiness score */}
      {readiness && (
        <div className="mb-4" data-testid="readiness-score">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <Gauge size={15} className="text-orange" /> Campaign Readiness: {readiness.score}%
              <span className={`text-xs px-2 py-0.5 rounded-full border ${
                readiness.verdict === 'Ready to Launch' ? 'bg-lime/15 text-lime border-lime/40'
                  : readiness.verdict === 'Needs Review' ? 'bg-orange/15 text-orange border-orange/40'
                    : 'bg-red-500/15 text-red-400 border-red-500/40'
              }`} data-testid="readiness-verdict">{readiness.verdict}</span>
            </p>
            <button onClick={() => setShowChecks(!showChecks)} data-testid="readiness-toggle" className="text-xs text-gray hover:text-white">
              {showChecks ? 'Hide checklist' : 'See what\u2019s missing'}
            </button>
          </div>
          <div className="h-2 rounded-full bg-black overflow-hidden">
            <div className={`h-full rounded-full ${readiness.score >= 90 ? 'bg-lime' : readiness.score >= 60 ? 'bg-orange' : 'bg-red-500'}`}
              style={{ width: `${readiness.score}%` }} />
          </div>
          {showChecks && (
            <div className="mt-3 space-y-1.5" data-testid="readiness-checklist">
              {readiness.checks.map((c) => (
                <div key={c.label} className="flex items-start gap-2 text-xs">
                  {c.ok ? <CheckCircle2 size={13} className="text-lime mt-0.5 shrink-0" /> : <XCircle size={13} className="text-orange mt-0.5 shrink-0" />}
                  <span className="text-light-gray">{c.label}{!c.ok && c.fix && <span className="text-gray"> — {c.fix}</span>}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Check Performance */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <button onClick={runCheck} disabled={checking} data-testid="check-performance-btn"
          className="px-4 py-2 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50 flex items-center gap-2">
          <Activity size={15} /> {checking ? 'Checking…' : 'Check Performance'}
        </button>
        <button onClick={() => setOpen(!open)} data-testid="full-numbers-toggle"
          className="text-sm text-gray hover:text-white flex items-center gap-1">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Enter full campaign numbers
        </button>
      </div>

      {check && (
        <div className={`p-4 rounded-lg border mb-4 ${STATUS_COLORS[check.status]}`} data-testid="performance-check-result">
          <p className="font-black text-lg mb-1" data-testid="performance-status">{check.headline}</p>
          <p className="text-sm text-light-gray mb-2">{check.explanation}</p>
          {check.insights.length > 0 && (
            <ul className="space-y-1 mb-2" data-testid="performance-insights">
              {check.insights.map((i) => <li key={i} className="text-xs text-light-gray flex gap-1.5"><span>•</span>{i}</li>)}
            </ul>
          )}
          {check.pacing.length > 0 && (
            <div className="pt-2 border-t border-white/10" data-testid="performance-pacing">
              <p className="text-[13px] font-bold uppercase tracking-wide mb-1 opacity-70">Budget pacing</p>
              {check.pacing.map((p) => <p key={p} className="text-xs text-light-gray flex gap-1.5"><span>→</span>{p}</p>)}
            </div>
          )}
        </div>
      )}

      {/* KPI chips */}
      {hasKpis && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="kpi-computed-chips">
          {Object.entries(KPI_LABELS).map(([k, label]) => kpis[k] != null && (
            <span key={k} className="px-2.5 py-1 rounded-full bg-black border border-divider text-xs">
              <span className="text-gray">{label}: </span>
              <span className="text-lime font-bold">
                {k === 'close_rate' || k === 'roi' ? `${kpis[k]}%` : k === 'roas' ? `${kpis[k]}x` : `$${kpis[k]}`}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Auto-tracked data — never blended */}
      {perf.auto && (
        <div className="p-3 rounded-lg bg-black border border-divider mb-3 text-xs" data-testid="auto-tracked-block">
          <span className="text-lime font-bold">Auto-tracked:</span>{' '}
          <span className="text-light-gray">{perf.auto.booked_jobs} booked job{perf.auto.booked_jobs !== 1 ? 's' : ''}
            {perf.auto.revenue > 0 ? ` · $${perf.auto.revenue.toLocaleString()} revenue` : ''} — {perf.auto.label}</span>
        </div>
      )}

      {open && (
        <div data-testid="full-numbers-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            {FIELD_GROUPS.map((g) => (
              <div key={g.title} className="p-3 rounded-lg bg-black border border-divider">
                <p className="text-xs text-muted uppercase tracking-wide font-bold mb-2">{g.title}</p>
                <div className="grid grid-cols-2 gap-2">
                  {g.fields.map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-[13px] text-gray mb-0.5">{label}</label>
                      <input type="number" min="0" value={form[k] ?? ''} data-testid={`perf-input-${k}`}
                        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                        className="w-full bg-card-bg border border-divider rounded px-2 py-1.5 text-sm text-white focus:border-lime focus:outline-none" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes — what changed, what you tried…"
            data-testid="perf-notes-input" rows={2}
            className="w-full bg-black border border-divider rounded-lg px-3 py-2 text-sm text-white focus:border-lime focus:outline-none mb-2" />
          <button onClick={save} disabled={saving} data-testid="perf-save-btn"
            className="px-4 py-2 rounded-lg bg-orange text-black font-bold text-sm hover:bg-orange/90 disabled:opacity-50 flex items-center gap-1.5">
            <Save size={14} /> {saving ? 'Saving…' : 'Save Numbers'}
          </button>
          <p className="text-[13px] text-gray mt-2">
            GritClicks never invents revenue — if you don't enter booking or revenue numbers, it only reports leads and calls.
          </p>
        </div>
      )}
    </div>
  );
};
